import { FadeIn, Overline } from "@/components/Reveal";
import { TrendingUp, Globe, FlaskConical, Cpu, GraduationCap, Users, Rocket } from "lucide-react";

const CATEGORIES = [
  {
    title: "Business & Economics",
    desc: "Channels analysing markets, entrepreneurship, companies and the forces shaping the economy.",
    img: "https://images.unsplash.com/photo-1745509267699-1b1db256601e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGVjb25vbXklMjBncmFwaCUyMGFic3RyYWN0fGVufDB8fHx8MTc4NDE5MDcxMnww&ixlib=rb-4.1.0&q=85",
    icon: TrendingUp,
    span: "md:col-span-7",
    tall: true,
  },
  {
    title: "Geopolitics & World Affairs",
    desc: "Creators explaining international relations, diplomacy and global power shifts.",
    img: "https://images.pexels.com/photos/10220276/pexels-photo-10220276.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Globe,
    span: "md:col-span-5",
    tall: true,
  },
  {
    title: "Science & Engineering",
    desc: "Rigorous explorations of physics, mathematics, engineering and how things are built.",
    img: "https://images.pexels.com/photos/3862631/pexels-photo-3862631.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: FlaskConical,
    span: "md:col-span-4",
  },
  {
    title: "Technology & AI",
    desc: "Analysis of artificial intelligence, software, innovation and the technology industry.",
    img: "https://images.pexels.com/photos/17485706/pexels-photo-17485706.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Cpu,
    span: "md:col-span-4",
  },
  {
    title: "Education & History",
    desc: "Story-driven learning across history, ideas and intellectual culture.",
    img: "https://images.pexels.com/photos/14215064/pexels-photo-14215064.jpeg",
    icon: GraduationCap,
    span: "md:col-span-4",
  },
  {
    title: "Society & Culture",
    desc: "Thoughtful examinations of social trends, culture and real-world issues.",
    img: "https://images.pexels.com/photos/2261165/pexels-photo-2261165.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Users,
    span: "md:col-span-6",
  },
  {
    title: "Space & Energy",
    desc: "Coverage of space exploration, energy systems and the natural world.",
    img: "https://images.unsplash.com/photo-1711560708511-36b48acad6bb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxzcGFjZSUyMHRlbGVzY29wZSUyMGdhbGF4eXxlbnwwfHx8fDE3OTI2ODgxNjB8MA&ixlib=rb-4.1.0&q=85",
    icon: Rocket,
    span: "md:col-span-6",
  },
];

const slug = (t) => t.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export const FocusGrid = () => (
  <section id="focus" className="py-24 md:py-32 bg-white border-y border-black/5" data-testid="focus-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <FadeIn className="max-w-3xl">
        <Overline>Areas of Focus</Overline>
        <h2 className="mt-6 font-serif text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight leading-tight">
          Seven worlds of informational YouTube.
        </h2>
        <p className="mt-6 text-base md:text-lg text-charcoal/70 leading-relaxed">
          We work with creators who educate, explain, analyse and investigate —
          across the subjects curious audiences return to again and again.
        </p>
      </FadeIn>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {CATEGORIES.map((c, i) => {
          const Icon = c.icon;
          return (
            <FadeIn key={c.title} delay={(i % 3) * 0.08} className={c.span}>
              <article
                className={`group relative overflow-hidden border border-black/10 ${
                  c.tall ? "h-[400px] md:h-[440px]" : "h-[340px] md:h-[360px]"
                }`}
                data-testid={`focus-card-${slug(c.title)}`}
              >
                <img
                  src={c.img}
                  alt={`${c.title} — informational YouTube category`}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover grayscale-[0.7] transition-all duration-700 group-hover:grayscale-0 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-8">
                  <Icon className="h-5 w-5 text-mutedblue mb-4" aria-hidden="true" />
                  <h3 className="font-serif text-2xl md:text-3xl tracking-tight text-white">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/70 leading-relaxed max-w-md">
                    {c.desc}
                  </p>
                </div>
              </article>
            </FadeIn>
          );
        })}
      </div>
    </div>
  </section>
);
